import { useLayoutEffect } from 'react'
import { motion } from 'motion/react'
import { pageVariants } from './variants'
import { useCompactViewport } from './useMediaQuery'

interface PageTransitionProps {
  children: React.ReactNode
  /** Pathname of the page being shown. Used to reset scroll when it changes. */
  pathname: string
  hash?: string
}

/**
 * Enter/exit wrapper for one public page. Render it inside <AnimatePresence>
 * keyed by pathname so the outgoing page fades out before the next fades in.
 */
export function PageTransition({ children, pathname, hash }: PageTransitionProps) {
  // New page mounted (after the previous one exited): start it at the top,
  // unless the URL targets an in-page anchor.
  useLayoutEffect(() => {
    if (!hash) {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
    }
  }, [pathname, hash])

  // Static on phones (see ScrollReveal): route changes switch instantly there.
  const compact = useCompactViewport()
  if (compact) {
    return <div>{children}</div>
  }

  return (
    <motion.div variants={pageVariants} initial="initial" animate="enter" exit="exit">
      {children}
    </motion.div>
  )
}
